import React, {useCallback} from 'react';
import {getBowserState} from '@epic-core/common';
import {AccentColorOptions, useBreakpointDown} from 'epic-ue-theme';
import {Typography} from '../../typography';
import {
    CreatorCardWrapper,
    CreatorCardImage,
    CreatorCardActive,
    CreatorCardInactive
} from './CreatorCard.styles';
import {Eyebrow, EyebrowProps} from '../../eyebrow';
import {Image, ImageProps} from '../../images';
import HtmlVideoEmbed from 'epic-react-common/src/components/HtmlVideoEmbed';
import {ArrowLeft} from '../../icons';
import {Button} from '../../buttons';
import {OutlinedCard} from '../outlined/OutlinedCard';
import {MouseHijack, UeLink} from '../../../wraps';
import {useCommunityActiveIndex} from '../../../layout/galleries/CommunityGallery.hooks';
import styled from 'styled-components';

const Overlay = styled.div<{color: string}>`
    width: 100px;
    height: 100px;
    border-radius: 899999px;
    border: 2px solid ${(p) => p.color && `${p.theme.palette.accent[p.color]}`};
    background: rgba(14, 17, 40, 0.6);
    position: absolute;
    top: 0;
    left: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    text-align: center;
    visibility: hidden;
    text-align: center;
    svg {
        font-size: 2.5em;
        text-align: center;
    }
    .ue-icon-inner-play {
        margin: 5px 0 0 10px;
        font-size: 2.5em;
    }
`;

export interface BaseCreatorCardProps {
    accentColor?: AccentColorOptions;
    image?: ImageProps;
    title?: string;
    description?: string;
    href?: string;
    buttonText?: string;
    className?: string;
    color?: 'pearl' | 'chalk';
    eyebrow?: EyebrowProps;
    onChange?: (arg: number | null) => void;
    cardIndex?: number;
    active?: boolean;
    video?: string;
    notActiveSlide?: boolean;
}

export interface CreatorImageCardProps extends BaseCreatorCardProps {
    eyebrow: EyebrowProps;
}

export interface CreatorVideoCardProps extends BaseCreatorCardProps {
    onChange: (arg: number | null) => void;
    cardIndex: number;
    active?: boolean;
    video: string;
}

export type CreatorCardProps = CreatorImageCardProps | CreatorVideoCardProps;

export const CreatorCard = (props: CreatorCardProps): JSX.Element => {
    const isSm = useBreakpointDown('sm');
    const {
        accentColor,
        eyebrow,
        image,
        title,
        href,
        description,
        className,
        color,
        video,
        cardIndex = null,
        active,
        buttonText = 'Read more',
        notActiveSlide
    } = props;

    const {setCommunityActiveIndex} = useCommunityActiveIndex();

    const handleChange = useCallback(() => {
        if (active) {
            setCommunityActiveIndex(null);
        } else {
            setCommunityActiveIndex(cardIndex);
        }
    }, [setCommunityActiveIndex, active, cardIndex]);

    const cardType = video ? 'video-card' : 'image-card';
    const cardColor = color ? color : 'pearl';
    const rootClasses = [
        'creator-card',
        cardType,
        cardColor,
        className,
        active ? 'active' : 'inactive'
    ].join(' ');
    const {mobile, tablet} = getBowserState();

    const cardCopy = (
        <>
            {title && (
                <Typography variant="h3" big>
                    {title}
                </Typography>
            )}
            {description && (
                <Typography variant="p" caption={!video}>
                    {description}
                </Typography>
            )}
            {href && (
                <Button
                    tabIndex={notActiveSlide ? -1 : 0}
                    type={cardType === 'image-card' ? 'secondary' : 'tertiary'}
                    color={accentColor}
                    href={href}>
                    {buttonText}
                </Button>
            )}
        </>
    );

    return (
        <CreatorCardWrapper classes={{root: rootClasses}}>
            {video && <OutlinedCard accentColor={accentColor} />}
            {eyebrow && (eyebrow.text || eyebrow.children) && <Eyebrow {...eyebrow} />}
            {!video && image && (
                <CreatorCardImage>
                    <Image
                        src={image.src}
                        ratio={video ? 'rect' : 'square'}
                        width={isSm ? 164 : 220}
                        alt={image.alt ? image.alt : 'Image of creator'}
                    />
                </CreatorCardImage>
            )}
            {video && (
                <>
                    {active ? (
                        <CreatorCardActive>
                            <UeLink tabIndex={notActiveSlide ? -1 : 0} onClick={handleChange}>
                                <ArrowLeft />
                            </UeLink>
                            <HtmlVideoEmbed
                                content={[video]}
                                play={true}
                                mute={false}
                                controls={true}
                            />
                        </CreatorCardActive>
                    ) : (
                        <CreatorCardInactive>
                            {isSm || mobile || tablet ? (
                                <>
                                    <UeLink
                                        tabIndex={notActiveSlide ? -1 : 0}
                                        onClick={handleChange}>
                                        <Overlay
                                            color={accentColor || 'sky'}
                                            className="video-card-play-icon">
                                            <Typography variant="button" big={true}>
                                                Play
                                            </Typography>
                                        </Overlay>
                                        <Image
                                            src={image && image.src}
                                            fit="cover"
                                            disableRatioHelper={true}
                                            alt={
                                                image && image.alt ? image.alt : 'Image of creator'
                                            }
                                            height={366}
                                        />
                                    </UeLink>
                                </>
                            ) : (
                                <MouseHijack
                                    hoverCursorType="play"
                                    onClick={handleChange}
                                    accentColor={accentColor}>
                                    <Image
                                        src={image && image.src}
                                        fit="cover"
                                        disableRatioHelper={true}
                                        alt={image && image.alt ? image.alt : 'Image of creator'}
                                        height={366}
                                    />
                                </MouseHijack>
                            )}
                        </CreatorCardInactive>
                    )}
                </>
            )}
            {video ? <div className={`copy-container ${cardColor}`}>{cardCopy}</div> : cardCopy}
        </CreatorCardWrapper>
    );
};
